import { CheckIcon, PlusIcon } from 'lucide-react'

import { FreightEditorAudience } from '@/components/freight/freightEditorAudience'
import { Button } from '@/components/ui/button'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { cn } from '@/lib/utils'
import { tagKey } from '@/utils/tagText'

export interface FmcsaCargo {
  name: string
  generic: boolean
}

const LABEL: Record<FreightEditorAudience, string> = {
  [FreightEditorAudience.STAFF]: 'Registered with FMCSA',
  [FreightEditorAudience.CUSTOMER]: "Hint: Here's what your fleet has registered with the FMCSA",
}

interface FmcsaCargoHintsProps {
  cargo: FmcsaCargo[]
  added: string[]
  audience: FreightEditorAudience
  disabled?: boolean
  onAdd: (name: string) => void
}

/** The carrier's FMCSA cargo types as one-click tags; generic ones are greyed out and say why in a popover. */
export function FmcsaCargoHints({ cargo, added, audience, disabled, onAdd }: FmcsaCargoHintsProps) {
  if (cargo.every((item) => item.generic)) return null
  const taken = new Set(added.map(tagKey))

  return (
    <div className="flex flex-col gap-2">
      <p
        className={cn(
          'text-muted-foreground',
          audience === FreightEditorAudience.CUSTOMER ? 'text-xs' : 'text-sm'
        )}
      >
        {LABEL[audience]}
      </p>
      <div className="flex flex-wrap gap-2">
        {cargo.map((item) =>
          item.generic ? (
            <TooGenericChip key={item.name} name={item.name} />
          ) : (
            <CargoChip
              key={item.name}
              name={item.name}
              isAdded={taken.has(tagKey(item.name))}
              disabled={disabled}
              onAdd={onAdd}
            />
          )
        )}
      </div>
    </div>
  )
}

function CargoChip({
  name,
  isAdded,
  disabled,
  onAdd,
}: {
  name: string
  isAdded: boolean
  disabled?: boolean
  onAdd: (name: string) => void
}) {
  return (
    <Button
      type="button"
      variant="outlineThin"
      size="xs"
      disabled={disabled || isAdded}
      onClick={() => onAdd(name)}
    >
      {isAdded ? <CheckIcon aria-hidden /> : <PlusIcon aria-hidden />}
      {name}
    </Button>
  )
}

// Not `disabled`: a disabled button can't open the popover that says why.
function TooGenericChip({ name }: { name: string }) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outlineThin"
          size="xs"
          aria-disabled
          className="bg-ink-50 border-ink-100 text-ink-500 hover:bg-ink-50 cursor-not-allowed"
        >
          {name}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-auto max-w-72 p-3 text-sm">
        {name} is not specific enough for most insurance providers.
      </PopoverContent>
    </Popover>
  )
}
